import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Heart, MoreVertical, Eraser, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImg } from "../lib/api";
import BottomNav from "../components/BottomNav";

const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const now = new Date();
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const diffDays = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
  if (diffDays === 0) return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  if (diffDays === 1) return "Hier";
  if (diffDays < 7) return d.toLocaleDateString("fr-FR", { weekday: "short" });
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

const CONFIRM_TEXTS = {
  conv: { title: "Vider la conversation ?", desc: "Tous les messages seront supprimés pour vous deux. Le match est conservé.", btn: "Vider" },
  match: { title: "Retirer ce match ?", desc: "Le match et toute la conversation seront supprimés pour vous deux, sans notification. Vous pourrez vous recroiser dans Découvrir.", btn: "Retirer" },
  allConv: { title: "Vider toutes les conversations ?", desc: "Tous les messages de toutes vos conversations seront supprimés. Vos matchs sont conservés.", btn: "Tout vider" },
  allMatches: { title: "Retirer tous les matchs ?", desc: "Tous vos matchs et toutes vos conversations seront définitivement supprimés.", btn: "Tout retirer" },
};

const Matches = () => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuFor, setMenuFor] = useState(null);
  const [eraseOpen, setEraseOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // {type, match?}
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/matches");
      setMatches(data);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const runConfirm = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (confirm.type === "conv") {
        await api.delete(`/conversations/${confirm.match.match_id}/messages`);
        toast.success("Conversation vidée.");
      } else if (confirm.type === "match") {
        await api.delete(`/matches/${confirm.match.match_id}`);
        toast.success("Match retiré.");
      } else if (confirm.type === "allConv") {
        const { data } = await api.delete("/conversations/messages");
        toast.success(`${data.deleted} message(s) supprimé(s).`);
      } else if (confirm.type === "allMatches") {
        const { data } = await api.delete("/matches");
        toast.success(`${data.deleted} match(s) retiré(s).`);
      }
      setConfirm(null);
      setEraseOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action impossible.");
    }
    setBusy(false);
  };

  const texts = confirm ? CONFIRM_TEXTS[confirm.type] : null;

  return (
    <div className="min-h-screen bg-kora-sand pb-28" onClick={() => setMenuFor(null)}>
      <div className="max-w-md mx-auto px-5 pt-6">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h1 className="font-display font-black text-3xl text-kora-ink">Vos matchs</h1>
          {matches.length > 0 && (
            <button
              data-testid="matches-erase-all-btn"
              onClick={(e) => { e.stopPropagation(); setEraseOpen(true); }}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-kora-muted hover:text-kora-terracotta transition-colors mt-2"
            >
              <Eraser size={15} /> Tout effacer
            </button>
          )}
        </div>
        <p className="text-kora-ink/60 mb-6">Discutez avec vos coups de cœur.</p>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-9 h-9 border-4 border-kora-terracotta border-t-transparent rounded-full animate-spin" />
          </div>
        ) : matches.length === 0 ? (
          <div data-testid="no-matches" className="bg-white rounded-3xl border border-kora-line p-8 text-center mt-8">
            <div className="w-16 h-16 rounded-full bg-kora-sand flex items-center justify-center mx-auto mb-4">
              <Heart size={28} className="text-kora-terracotta" />
            </div>
            <p className="font-display font-bold text-xl text-kora-ink">Pas encore de match</p>
            <p className="text-kora-muted text-sm mt-1">Continuez à découvrir des profils !</p>
            <button onClick={() => navigate("/decouvrir")} className="mt-5 bg-kora-terracotta text-white rounded-full px-6 py-3 font-semibold">Découvrir</button>
          </div>
        ) : (
          <div className="space-y-3">
            {matches.map((m) => (
              <div
                key={m.match_id}
                data-testid="match-item"
                onClick={() => navigate(`/chat/${m.match_id}`)}
                className="relative w-full flex items-center gap-4 bg-white rounded-2xl p-3 border border-kora-line hover:border-kora-terracotta/40 transition-colors text-left cursor-pointer"
              >
                <div className="relative shrink-0">
                  <img src={resolveImg(m.profile.photo_url)} alt={m.profile.name} className="w-14 h-14 rounded-full object-cover" />
                  {m.profile.presence?.online && (
                    <span data-testid={`match-online-dot-${m.match_id}`} className="absolute bottom-0.5 right-0.5 w-3.5 h-3.5 rounded-full bg-green-500 border-2 border-white" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="font-display font-bold text-kora-ink truncate">{m.profile.name}, {m.profile.age}</p>
                    {m.last_message && (
                      <span data-testid={`match-time-${m.match_id}`} className={`text-[11px] shrink-0 ${m.unread > 0 ? "text-kora-terracotta font-semibold" : "text-kora-muted"}`}>
                        {formatTime(m.last_activity)}
                      </span>
                    )}
                  </div>
                  <p className={`text-sm truncate ${m.unread > 0 ? "text-kora-ink font-semibold" : "text-kora-muted"}`}>{m.last_message || "Dites bonjour 👋"}</p>
                </div>
                <div className="relative shrink-0 flex items-center gap-1">
                  <div className="relative">
                    <MessageCircle size={20} className={m.unread > 0 ? "text-kora-terracotta" : "text-kora-muted"} />
                    {m.unread > 0 && (
                      <span
                        data-testid={`match-unread-${m.match_id}`}
                        className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-kora-terracotta text-white text-[10px] font-bold leading-none border border-white"
                      >
                        {m.unread > 9 ? "9+" : m.unread}
                      </span>
                    )}
                  </div>
                  <button
                    data-testid={`match-menu-btn-${m.match_id}`}
                    onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === m.match_id ? null : m.match_id); }}
                    className="p-1.5 rounded-full text-kora-muted hover:bg-kora-sand hover:text-kora-ink transition-colors"
                    aria-label="Options"
                  >
                    <MoreVertical size={18} />
                  </button>
                </div>
                {menuFor === m.match_id && (
                  <div
                    data-testid={`match-menu-${m.match_id}`}
                    className="absolute right-2 top-14 z-30 bg-white border border-kora-line rounded-2xl shadow-lg overflow-hidden w-56"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      data-testid={`match-clear-conv-btn-${m.match_id}`}
                      onClick={() => { setMenuFor(null); setConfirm({ type: "conv", match: m }); }}
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm text-kora-ink hover:bg-kora-sand text-left"
                    >
                      <Eraser size={15} className="text-kora-muted" /> Vider la conversation
                    </button>
                    <button
                      data-testid={`match-remove-btn-${m.match_id}`}
                      onClick={() => { setMenuFor(null); setConfirm({ type: "match", match: m }); }}
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-500 hover:bg-red-50 text-left border-t border-kora-line"
                    >
                      <Trash2 size={15} /> Retirer le match
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {eraseOpen && !confirm && (
        <div className="fixed inset-0 z-[80] bg-kora-ink/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4" data-testid="erase-all-modal" onClick={() => setEraseOpen(false)}>
          <div className="bg-white w-full md:max-w-sm rounded-3xl p-6 animate-float-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-black text-lg text-kora-ink">Tout effacer</h3>
              <button data-testid="erase-all-close" onClick={() => setEraseOpen(false)} className="text-kora-muted hover:text-kora-ink"><X size={20} /></button>
            </div>
            <button
              data-testid="erase-all-conversations-btn"
              onClick={() => { setEraseOpen(false); setConfirm({ type: "allConv" }); }}
              className="w-full flex items-center gap-3 p-4 rounded-2xl border border-kora-line hover:border-kora-terracotta/50 text-left mb-2"
            >
              <Eraser size={20} className="text-kora-terracotta shrink-0" />
              <span>
                <span className="block font-bold text-sm text-kora-ink">Vider toutes les conversations</span>
                <span className="block text-xs text-kora-muted">Supprime les messages, garde vos matchs</span>
              </span>
            </button>
            <button
              data-testid="erase-all-matches-btn"
              onClick={() => { setEraseOpen(false); setConfirm({ type: "allMatches" }); }}
              className="w-full flex items-center gap-3 p-4 rounded-2xl border border-red-200 hover:border-red-400 text-left"
            >
              <Trash2 size={20} className="text-red-500 shrink-0" />
              <span>
                <span className="block font-bold text-sm text-red-500">Retirer tous les matchs</span>
                <span className="block text-xs text-kora-muted">Supprime matchs et conversations définitivement</span>
              </span>
            </button>
          </div>
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 z-[85] bg-kora-ink/70 backdrop-blur-sm flex items-end md:items-center justify-center p-4" data-testid="confirm-modal" onClick={() => setConfirm(null)}>
          <div className="bg-white w-full md:max-w-sm rounded-3xl p-6 animate-float-up text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-14 h-14 rounded-full bg-red-50 text-red-500 flex items-center justify-center mx-auto mb-3">
              {confirm.type === "conv" || confirm.type === "allConv" ? <Eraser size={26} /> : <Trash2 size={26} />}
            </div>
            <h3 className="font-display font-black text-lg text-kora-ink">{texts.title}</h3>
            <p className="text-sm text-kora-muted mt-1 mb-5">
              {confirm.match ? <><strong>{confirm.match.profile.name}</strong> — </> : null}
              {texts.desc}
            </p>
            <div className="flex gap-2">
              <button data-testid="confirm-cancel-btn" onClick={() => setConfirm(null)} className="flex-1 py-2.5 rounded-full border border-kora-line font-semibold text-kora-ink">Annuler</button>
              <button data-testid="confirm-ok-btn" onClick={runConfirm} disabled={busy} className="flex-1 py-2.5 rounded-full bg-red-500 text-white font-bold disabled:opacity-60">
                {busy ? "…" : texts.btn}
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default Matches;
